<script setup lang="ts">
    import { ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { copyCode as apiCopyCode, sqlQuery } from '@/shared/helpers/api';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { http } from '@/shared/httpWrapper';
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import CodeSetSelectorModal from '@/shared/components/codesetselector/CodeSetSelectorModal.vue';

    const props = defineProps<{
        codeId: number;
        codeIdentity: string;
        codeName: string;
        codeSetName: string;
        libraryName: string;
    }>();

    const buefy = useBuefy();
    const { showError } = useShow();
    const router = useRouter();
    const showSelector = ref<boolean>(false);
    const existingScopes = ref<string[]>([]);

    async function openSelector() {
        existingScopes.value = [];
        showSelector.value = true;
        try {
            const existing = await sqlQuery(http, {
                query: `SELECT Scope FROM ${props.libraryName} WHERE [Identity] = @Identity`,
                arguments: { Identity: props.codeIdentity }
            });
            existingScopes.value = existing.data.map(row => row.Scope as string);
        } catch {
            // The "already exists" tags are informational only, so the copy can go ahead without them
        }
    }

    function onTargetsSelected(targetCodeSets: string[]) {
        showSelector.value = false;
        const codeSetText = targetCodeSets.length === 1 ? '1 library set' : `${targetCodeSets.length} library sets`;
        buefy.dialog.confirm({
            title: 'Copy to other scopes',
            message: `This will copy the code to ${codeSetText}. Where the code already exists it will be updated. A release will be used. Any active subscriptions will trigger code publication. Continue?`,
            type: 'is-warning',
            hasIcon: true,
            onConfirm: async () => {
                try {
                    const response = await apiCopyCode(http, props.codeId, targetCodeSets);
                    await router.push(getReleaseLink(response.data.id));
                } catch (error) {
                    showError(error);
                }
            }
        });
    }
</script>

<template>
    <b-button
        v-require-can-edit-code="{ libraryName }"
        icon-left="content-duplicate"
        type="is-info"
        @click="openSelector">
        Copy to other scopes
    </b-button>
    <code-set-selector-modal
        v-if="showSelector"
        :library-name="libraryName"
        :source-code-set-name="codeSetName"
        :existing-scopes="existingScopes"
        :title="`Copy ${codeName} to other scopes`"
        subtitle="Select the scopes to copy the code to. Scopes that share a library set are selected together."
        confirm-label="Copy"
        @confirm="onTargetsSelected"
        @cancel="showSelector = false" />
</template>
